import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n/config';

export interface CopilotMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface UseMatchCopilotReturn {
  messages: CopilotMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (text: string) => Promise<void>;
  clearHistory: () => void;
  remainingMessages: number;
  gameRemainingMessages: number;
  isAuthenticated: boolean;
  requiresAuth: boolean;
  clearAuthRequired: () => void;
  isBalanceLoaded: boolean;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/match-copilot-chat`;
const STORAGE_KEY_PREFIX = 'copilot-history-';
const MAX_MESSAGES_DISPLAY = 50;

export function useMatchCopilot(fixtureId: number | null): UseMatchCopilotReturn {
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remainingMessages, setRemainingMessages] = useState(30);
  const [gameRemainingMessages, setGameRemainingMessages] = useState(5);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [isBalanceLoaded, setIsBalanceLoaded] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsAuthenticated(!!session);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // Fetch real balance from DB on mount
  useEffect(() => {
    if (!fixtureId || !isAuthenticated) {
      setIsBalanceLoaded(true);
      return;
    }
    setIsBalanceLoaded(false);

    const fetchBalance = async () => {
      const { data, error } = await supabase
        .from('copilot_rate_limits')
        .select('hourly_count, game_count, hour_window')
        .eq('fixture_id', fixtureId)
        .maybeSingle();

      if (error || !data) return; // No record = full balance (defaults)

      const currentHour = new Date();
      currentHour.setMinutes(0, 0, 0);
      const windowDate = new Date(data.hour_window);
      const hourlyCount = windowDate < currentHour ? 0 : (data.hourly_count ?? 0);

      setGameRemainingMessages(Math.max(0, 5 - (data.game_count ?? 0)));
      setRemainingMessages(Math.max(0, 30 - hourlyCount));
      setIsBalanceLoaded(true);
    };

    fetchBalance();
  }, [fixtureId, isAuthenticated]);

  // Load history from localStorage on mount
  useEffect(() => {
    if (!fixtureId) return;
    
    const storageKey = `${STORAGE_KEY_PREFIX}${fixtureId}`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as CopilotMessage[];
        setMessages(parsed.slice(-MAX_MESSAGES_DISPLAY));
      }
    } catch (e) {
      console.warn('[useMatchCopilot] Failed to load history:', e);
    }
  }, [fixtureId]);

  // Save history to localStorage when messages change
  useEffect(() => {
    if (!fixtureId || messages.length === 0) return;
    
    const storageKey = `${STORAGE_KEY_PREFIX}${fixtureId}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages.slice(-MAX_MESSAGES_DISPLAY)));
    } catch (e) {
      console.warn('[useMatchCopilot] Failed to save history:', e);
    }
  }, [messages, fixtureId]);

  const sendMessage = useCallback(async (text: string) => {
    if (!fixtureId || !text.trim()) return;
    
    // Check authentication first
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setRequiresAuth(true);
      return;
    }
    setRequiresAuth(false);
    
    setError(null);
    const userMessage: CopilotMessage = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    let assistantContent = '';
    
    const upsertAssistant = (chunk: string) => {
      assistantContent += chunk;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return prev.map((m, i) => 
            i === prev.length - 1 ? { ...m, content: assistantContent } : m
          );
        }
        return [...prev, { role: 'assistant', content: assistantContent }];
      });
    };

    try {
      const allMessages = [...messages, userMessage].map(m => ({
        role: m.role,
        content: m.content,
      }));

      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ 
          fixture_id: fixtureId, 
          messages: allMessages,
          language: i18n.language,
        }),
        signal: abortControllerRef.current.signal,
      });

      // Update rate limit from headers (exposed via Access-Control-Expose-Headers)
      const remainingHeader = resp.headers.get('X-Rate-Limit-Remaining');
      const gameRemainingHeader = resp.headers.get('X-Game-Messages-Remaining');
      console.log('[useMatchCopilot] Rate limit headers:', { remainingHeader, gameRemainingHeader });
      if (remainingHeader) setRemainingMessages(parseInt(remainingHeader, 10));
      if (gameRemainingHeader) setGameRemainingMessages(parseInt(gameRemainingHeader, 10));

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        
        if (resp.status === 401) {
          setIsAuthenticated(false);
          throw new Error(i18n.t('errors:sessionExpired'));
        }
        if (resp.status === 429) {
          // Update remaining from error response
          if (typeof errorData.hourlyRemaining === 'number') {
            setRemainingMessages(errorData.hourlyRemaining);
          }
          if (typeof errorData.gameRemaining === 'number') {
            setGameRemainingMessages(errorData.gameRemaining);
          }
          const reason = errorData.reason === 'game_limit' 
            ? i18n.t('errors:gameLimitReached') 
            : i18n.t('errors:hourlyLimitReached');
          throw new Error(errorData.error || reason);
        }
        if (resp.status === 402) {
          // Budget limit exceeded
          throw new Error(errorData.error || i18n.t('errors:budgetLimitReached'));
        }
        throw new Error(errorData.error || i18n.t('errors:failedToGetResponse'));
      }

      if (!resp.body) {
        throw new Error('No response stream');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) upsertAssistant(content);
          } catch {
            // Incomplete JSON, put it back
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Final flush
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split('\n')) {
          if (!raw) continue;
          if (raw.endsWith('\r')) raw = raw.slice(0, -1);
          if (raw.startsWith(':') || raw.trim() === '') continue;
          if (!raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) upsertAssistant(content);
          } catch { /* ignore */ }
        }
      }

    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was aborted, ignore
        return;
      }
      console.error('[useMatchCopilot] Error:', err);
      setError(err instanceof Error ? err.message : i18n.t('errors:failedToSendMessage'));
      // Remove the empty assistant message if there was an error
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && !last.content) {
          return prev.slice(0, -1);
        }
        return prev;
      });
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [fixtureId, messages]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    setError(null);
    if (fixtureId) {
      const storageKey = `${STORAGE_KEY_PREFIX}${fixtureId}`;
      localStorage.removeItem(storageKey);
    }
  }, [fixtureId]);

  const clearAuthRequired = useCallback(() => {
    setRequiresAuth(false);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearHistory,
    remainingMessages,
    gameRemainingMessages,
    isAuthenticated,
    requiresAuth,
    clearAuthRequired,
    isBalanceLoaded,
  };
}
